import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";

import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Collapse,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  FormControlLabel,
  IconButton,
  InputAdornment,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
  useTheme,
  TablePagination,
  Tooltip,
} from "@mui/material";
import {
  Add as AddIcon,
  CheckCircle as CheckCircleIcon,
  Close as CloseIcon,
  Cancel as CancelIcon,
  Description as DescriptionIcon,
  KeyboardArrowDown as KeyboardArrowDownIcon,
  KeyboardArrowRight as KeyboardArrowRightIcon,
  Search as SearchIcon,
  EditOutlined as EditOutlinedIcon,
  AccountTree as WorkflowIcon,
  DynamicForm as DynamicFormIcon,
} from "@mui/icons-material";
import DynamicTableField, {
  createBlankRow,
  resolveColumns,
} from "../forms/DynamicTableField";

import { getFormDetails } from "services/company/forms/formServices";
import { showSuccessToast, showErrorToast } from "utils/toastNotification";

import {
  approveRejectExistingRequisition,
  fetchRequisitionDetails,
  fetchRequisitions,
} from "store/features/company/requisition/requisitionActions";
import {
  selectRequisitionActionLoading,
  selectRequisitionDetails,
  selectRequisitionDetailsError,
  selectRequisitionDetailsLoading,
  selectRequisitionError,
  selectRequisitionLoading,
  selectRequisitions,
} from "store/features/company/requisition/requisitionSelectors";
import { selectCurrentUser } from "store/features/auth/authSelectors";
import usePermissions from "hooks/usePermissions";
import EditRequisitionModal from "./EditRequisitionModal";

import rolesSvg from "assets/folders/roles.svg";

const statusFilters = ["all", "SUBMITTED", "APPROVED", "REJECTED"];

/* =====================================================
   WORKFLOW STEP VISUAL COMPONENT
===================================================== */
const WorkflowStepper = ({ steps, isDark }) => {
  if (!steps || steps.length === 0) return null;

  const stepTypeLabel = (type) => {
    if (type === "employee") return "Employee";
    if (type === "department") return "Department";
    if (type === "role") return "Role";
    return type;
  };

  const getStepColors = (status) => {
    if (status === "done") {
      return {
        main: "#16a34a",
        soft: isDark ? "rgba(22,163,74,0.12)" : "#f0fdf4",
        line: "#16a34a",
      };
    }

    if (status === "rejected") {
      return {
        main: "#dc2626",
        soft: isDark ? "rgba(220,38,38,0.12)" : "#fff1f2",
        line: "#dc2626",
      };
    }

    if (status === "pending") {
      return {
        main: "#2563eb",
        soft: isDark ? "rgba(37,99,235,0.14)" : "#eff6ff",
        line: isDark ? "#334155" : "#e5e7eb",
      };
    }

    return {
      main: isDark ? "#64748b" : "#9ca3af",
      soft: isDark ? "#111827" : "#f9fafb",
      line: isDark ? "#334155" : "#e5e7eb",
    };
  };

  const getStatusLabel = (status) => {
    if (status === "done") return "Approved";
    if (status === "pending") return "Awaiting";
    if (status === "rejected") return "Rejected";
    return status;
  };

  return (
    <Box sx={{ mt: 1 }}>
      {/* Header */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          mb: 2.5,
        }}
      >
        <Box>
          <Typography
            sx={{
              fontSize: "0.75rem",
              fontWeight: 700,
              color: isDark ? "#94a3b8" : "#6b7280",
              textTransform: "uppercase",
              letterSpacing: "0.08em",
            }}
          >
            Approval Steps
          </Typography>

          <Typography
            sx={{
              fontSize: "0.72rem",
              color: isDark ? "#64748b" : "#9ca3af",
              mt: 0.25,
            }}
          >
            {steps.length} {steps.length === 1 ? "step" : "steps"}
          </Typography>
        </Box>
      </Box>

      {/* Horizontal workflow */}
      <Box
        sx={{
          overflowX: "auto",
          pb: 1,
          "&::-webkit-scrollbar": {
            height: 5,
          },
          "&::-webkit-scrollbar-thumb": {
            backgroundColor: isDark ? "#334155" : "#d1d5db",
            borderRadius: 10,
          },
        }}
      >
        <Box
          sx={{
            display: "flex",
            minWidth: steps.length > 3 ? steps.length * 190 : "100%",
            alignItems: "flex-start",
          }}
        >
          {steps.map((step, idx) => {
            const colors = getStepColors(step.status);
            const isLast = idx === steps.length - 1;

            const isPending = step.status === "pending";
            const isDone = step.status === "done";
            const isRejected = step.status === "rejected";

            return (
              <Box
                key={step.id}
                sx={{
                  flex: 1,
                  minWidth: 170,
                  display: "flex",
                  alignItems: "flex-start",
                }}
              >
                {/* Step */}
                <Box
                  sx={{
                    flex: 1,
                    minWidth: 0,
                  }}
                >
                  {/* Step number + connector */}
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      width: "100%",
                      mb: 1.5,
                    }}
                  >
                    {/* Node */}
                    <Box
                      sx={{
                        width: 38,
                        height: 38,
                        flexShrink: 0,
                        borderRadius: "50%",
                        backgroundColor: colors.soft,
                        border: `2px solid ${colors.main}`,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        position: "relative",
                        zIndex: 2,

                        // ...(isPending && {
                        //   boxShadow: isDark
                        //     ? '0 0 0 5px rgba(37,99,235,0.10)'
                        //     : '0 0 0 5px rgba(37,99,235,0.08)',
                        // }),
                      }}
                    >
                      {isDone ? (
                        <Typography
                          sx={{
                            color: colors.main,
                            fontSize: 18,
                            fontWeight: 800,
                            lineHeight: 1,
                          }}
                        >
                          ✓
                        </Typography>
                      ) : isRejected ? (
                        <Typography
                          sx={{
                            color: colors.main,
                            fontSize: 17,
                            fontWeight: 800,
                            lineHeight: 1,
                          }}
                        >
                          ×
                        </Typography>
                      ) : (
                        <Typography
                          sx={{
                            color: colors.main,
                            fontSize: "0.75rem",
                            fontWeight: 800,
                          }}
                        >
                          {step.step_order}
                        </Typography>
                      )}
                    </Box>

                    {/* Connector */}
                    {!isLast && (
                      <Box
                        sx={{
                          height: 2,
                          flex: 1,
                          backgroundColor: colors.line,
                          mx: 1,
                          borderRadius: 2,
                        }}
                      />
                    )}
                  </Box>

                  {/* Step information */}
                  <Box
                    sx={{
                      pr: isLast ? 0 : 1.5,
                    }}
                  >
                    {/* Step label */}
                    <Typography
                      sx={{
                        fontSize: "0.65rem",
                        fontWeight: 700,
                        color: isDark ? "#64748b" : "#9ca3af",
                        textTransform: "uppercase",
                        letterSpacing: "0.06em",
                        mb: 0.35,
                      }}
                    >
                      Step {step.step_order}
                    </Typography>

                    {/* Approver */}
                    <Typography
                      sx={{
                        fontSize: "0.85rem",
                        fontWeight: 700,
                        color: isDark ? "#f1f5f9" : "#111827",
                        lineHeight: 1.35,
                        mb: 0.6,
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                      }}
                    >
                      {step.approver_name || "—"}
                    </Typography>

                    {/* Type + status */}
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 0.75,
                        flexWrap: "wrap",
                      }}
                    >
                      <Box
                        sx={{
                          px: 0.8,
                          py: 0.25,
                          borderRadius: "5px",
                          backgroundColor: isDark
                            ? "rgba(148,163,184,0.10)"
                            : "#f3f4f6",
                          color: isDark ? "#94a3b8" : "#6b7280",
                          fontSize: "0.62rem",
                          fontWeight: 600,
                        }}
                      >
                        {stepTypeLabel(step.approver_type)}
                      </Box>

                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          gap: 0.35,
                          color: colors.main,
                          fontSize: "0.62rem",
                          fontWeight: 700,
                        }}
                      >
                        <Box
                          sx={{
                            width: 5,
                            height: 5,
                            borderRadius: "50%",
                            backgroundColor: colors.main,
                          }}
                        />

                        {getStatusLabel(step.status)}
                      </Box>
                    </Box>

                    {/* Date */}
                    {step.action_at && (
                      <Typography
                        sx={{
                          fontSize: "0.65rem",
                          color: isDark ? "#64748b" : "#9ca3af",
                          mt: 0.7,
                        }}
                      >
                        {new Date(step.action_at).toLocaleDateString("en-IN", {
                          day: "2-digit",
                          month: "short",
                          year: "numeric",
                        })}
                      </Typography>
                    )}

                    {/* Action by */}
                    {step.action_by_name && (
                      <Typography
                        sx={{
                          fontSize: "0.65rem",
                          color: isDark ? "#64748b" : "#9ca3af",
                          mt: 0.15,
                        }}
                      >
                        by {step.action_by_name}
                      </Typography>
                    )}

                    {/* Remarks */}
                    {step.remarks && (
                      <Tooltip title={step.remarks} arrow>
                        <Typography
                          sx={{
                            fontSize: "0.65rem",
                            color: "#dc2626",
                            mt: 0.5,
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                            cursor: "default",
                          }}
                        >
                          {step.remarks}
                        </Typography>
                      </Tooltip>
                    )}

                    {/* Attached File */}
                    {step.file_url && (
                      <Box sx={{ mt: 0.7 }}>
                        <a
                          href={step.file_url}
                          target="_blank"
                          rel="noreferrer"
                          style={{
                            display: "inline-flex",
                            alignItems: "center",
                            gap: "4px",
                            fontSize: "0.68rem",
                            color: "#2563eb",
                            textDecoration: "none",
                            fontWeight: 600,
                          }}
                        >
                          <DescriptionIcon sx={{ fontSize: 13 }} />
                          View Document
                        </a>
                      </Box>
                    )}

                    {/* File required badge if awaiting */}
                    {step.is_file_required === 1 && !step.file_url && (
                      <Box
                        sx={{
                          display: "inline-flex",
                          mt: 0.5,
                          px: 0.6,
                          py: 0.15,
                          borderRadius: "4px",
                          backgroundColor: isDark
                            ? "rgba(234,179,8,0.12)"
                            : "#fef9c3",
                          color: isDark ? "#facc15" : "#854d0e",
                          fontSize: "0.58rem",
                          fontWeight: 700,
                        }}
                      >
                        File Required
                      </Box>
                    )}

                    {/* Final */}
                    {step.isFinal === 1 && (
                      <Box
                        sx={{
                          display: "inline-flex",
                          mt: 0.7,
                          px: 0.7,
                          py: 0.2,
                          borderRadius: "4px",
                          backgroundColor: isDark
                            ? "rgba(37,99,235,0.12)"
                            : "#eff6ff",
                          color: "#2563eb",
                          fontSize: "0.58rem",
                          fontWeight: 700,
                          textTransform: "uppercase",
                          letterSpacing: "0.04em",
                        }}
                      >
                        Final Step
                      </Box>
                    )}
                  </Box>
                </Box>
              </Box>
            );
          })}
        </Box>
      </Box>
    </Box>
  );
};

const formatCurrency = (value) => {
  if (value === null || value === undefined || value === "") return "-";
  const amount = Number(value);
  if (Number.isNaN(amount)) return value;

  return `INR ${amount.toLocaleString("en-IN", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
};

const formatDate = (value) => {
  if (!value) return "-";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "-";

  return date.toLocaleDateString("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
};

const getDisplayValue = (...values) => {
  const value = values.find(
    (item) => item !== null && item !== undefined && item !== "",
  );
  return value === undefined ? "-" : value;
};

const getStatusColor = (status, mode) => {
  const lightColors = {
    SUBMITTED: { bg: "#dbeafe", text: "#1e40af" },
    APPROVED: { bg: "#dcfce7", text: "#15803d" },
    REJECTED: { bg: "#fee2e2", text: "#991b1b" },
    PENDING: { bg: "#fef3c7", text: "#92400e" },
  };
  const darkColors = {
    SUBMITTED: { bg: "rgba(59,130,246,0.25)", text: "#93c5fd" },
    APPROVED: { bg: "rgba(34,197,94,0.25)", text: "#86efac" },
    REJECTED: { bg: "rgba(239,68,68,0.25)", text: "#fca5a5" },
    PENDING: { bg: "rgba(245,158,11,0.25)", text: "#fde68a" },
  };

  const colors = mode === "dark" ? darkColors : lightColors;
  return colors[status] || { bg: "#e5e7eb", text: "#374151" };
};

const hasAny = (hasPermission, permissions) =>
  permissions.some((permission) => hasPermission(permission));

const Requisitions = () => {
  const theme = useTheme();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { hasPermission } = usePermissions();

  const currentUser = useSelector(selectCurrentUser);
  const userRole = currentUser?.role || currentUser?.user_type;
  const loggedInEmpId = currentUser?.employee_id || currentUser?.id;

  const requisitions = useSelector(selectRequisitions);
  const loading = useSelector(selectRequisitionLoading);
  const error = useSelector(selectRequisitionError);
  const requisitionDetails = useSelector(selectRequisitionDetails);
  const detailsLoading = useSelector(selectRequisitionDetailsLoading);
  const detailsError = useSelector(selectRequisitionDetailsError);
  const actionLoading = useSelector(selectRequisitionActionLoading);

  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");

  // Pagination State
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };
  const [expandedRows, setExpandedRows] = useState({});
  const [actionDialog, setActionDialog] = useState({
    open: false,
    action: "",
    requisition: null,
    remarks: "",
    file: null,
    dynamicForm: null,
    formData: {},
    formLoading: false,
  });

  const [editModal, setEditModal] = useState({
    open: false,
    requisitionId: null,
  });

  const isDark = theme.palette.mode === "dark";
  const safeRequisitions = Array.isArray(requisitions) ? requisitions : [];

  const canView =
    hasAny(hasPermission, ["requisition.view", "requisitions.view"]) ||
    userRole === "Company";
  const canCreate =
    hasPermission("requisition.create") || userRole === "Company";

  // canApprove / canReject are now resolved PER ROW from the API (req.canApprove).
  // These global flags only determine whether the Actions column is visible at all.
  const canApproveGlobal =
    hasAny(hasPermission, [
      "requisition.approve",
      "requisition.approve_reject",
    ]) || userRole === "Company";
  const canEdit =
    hasAny(hasPermission, ["requisition.create", "requisition.update"]) ||
    userRole === "Company";
  const canDelete =
    hasAny(hasPermission, ["requisition.delete", "requisitions.delete"]) ||
    userRole === "Company";
  // Show Actions column if ANY requisition might have action buttons
  const canAction = canApproveGlobal || canEdit || canDelete;

  useEffect(() => {
    dispatch(fetchRequisitions());
  }, [dispatch]);

  const filteredRequisitions = safeRequisitions.filter((req) => {
    const searchableText = [
      req.requisition_no,
      req.requisition_type,
      req.project_name,
      req.department_name,
      req.requested_by_name,
      req.description,
      req.status,
    ]
      .filter(Boolean)
      .join(" ")
      .toLowerCase();

    const matchesSearch =
      searchTerm === "" || searchableText.includes(searchTerm.toLowerCase());
    const matchesStatus = statusFilter === "all" || req.status === statusFilter;

    return matchesSearch && matchesStatus;
  });

  const handleToggleDetails = (req) => {
    const requisitionId = req.id;
    const willExpand = !expandedRows[requisitionId];

    setExpandedRows((prev) => ({
      ...prev,
      [requisitionId]: willExpand,
    }));

    if (willExpand && requisitionId && !requisitionDetails[requisitionId]) {
      dispatch(fetchRequisitionDetails(requisitionId));
    }
  };

  const openActionDialog = async (requisition, action) => {
    const hasForm = Boolean(
      action === "APPROVE" && requisition?.pending_step_form_id,
    );
    setActionDialog({
      open: true,
      action,
      requisition,
      remarks: action === "APPROVE" ? "Approved" : "",
      file: null,
      dynamicForm: null,
      formData: {},
      formLoading: hasForm,
    });

    if (hasForm) {
      try {
        const res = await getFormDetails(requisition.pending_step_form_id);
        const resolved = res?.data?.data || res?.data || res;
        const isValid =
          res?.meta?.status !== false &&
          resolved &&
          (resolved.form_name || Array.isArray(resolved.fields));

        if (isValid) {
          const formObj = resolved;
          const initialFormValues = {};
          if (Array.isArray(formObj.fields)) {
            formObj.fields.forEach((f) => {
              initialFormValues[f.id] =
                f.field_type === "boolean"
                  ? false
                  : f.field_type === "table"
                  ? [createBlankRow(resolveColumns(f.options), 1)]
                  : "";
            });
          }
          setActionDialog((prev) => ({
            ...prev,
            dynamicForm: formObj,
            formData: initialFormValues,
            formLoading: false,
          }));
        } else {
          console.warn(
            "Dynamic form not found or inactive for ID:",
            requisition.pending_step_form_id,
          );
          setActionDialog((prev) => ({
            ...prev,
            dynamicForm: null,
            formLoading: false,
          }));
        }
      } catch (err) {
        console.error("Failed to load step form:", err);
        setActionDialog((prev) => ({
          ...prev,
          dynamicForm: null,
          formLoading: false,
        }));
      }
    }
  };

  const closeActionDialog = () => {
    setActionDialog({
      open: false,
      action: "",
      requisition: null,
      remarks: "",
      file: null,
      dynamicForm: null,
      formData: {},
      formLoading: false,
    });
  };

  const handleConfirmAction = async () => {
    const { requisition, action, remarks, file, dynamicForm, formData } =
      actionDialog;
    if (!requisition?.id || !action) return;
    if (action === "REJECT" && !remarks.trim()) return;
    if (
      action === "APPROVE" &&
      Number(requisition.pending_step_is_file_required) === 1 &&
      !file
    ) {
      return;
    }

    // Validate required dynamic form fields
    if (action === "APPROVE" && dynamicForm?.fields) {
      for (const field of dynamicForm.fields) {
        if (field.required) {
          const val = formData[field.id];
          if (field.field_type === "table") {
            if (
              !Array.isArray(val) ||
              val.length === 0 ||
              !val.some(
                (r) => r.description && String(r.description).trim() !== "",
              )
            ) {
              showErrorToast(
                `Please complete required table: "${
                  field.field_label || field.field_name
                }"`,
              );
              return;
            }
          } else if (
            val === undefined ||
            val === null ||
            String(val).trim() === ""
          ) {
            showErrorToast(
              `Please complete required field: "${
                field.field_label || field.field_name
              }"`,
            );
            return;
          }
        }
      }
    }

    let payload;
    if (file) {
      const fd = new FormData();
      fd.append("action", action);
      fd.append(
        "remarks",
        remarks.trim() || (action === "APPROVE" ? "Approved" : ""),
      );
      fd.append("file", file);
      if (dynamicForm) {
        fd.append("formData", JSON.stringify(formData));
      }
      payload = fd;
    } else {
      payload = {
        action,
        remarks: remarks.trim() || (action === "APPROVE" ? "Approved" : ""),
        formData: dynamicForm ? formData : undefined,
      };
    }

    const result = await dispatch(
      approveRejectExistingRequisition(requisition.id, payload),
    );

    if (result?.success) {
      closeActionDialog();
      dispatch(fetchRequisitions());
    }
  };

  if (!canView) {
    return (
      <Box sx={{ p: 4, display: "flex", justifyContent: "center" }}>
        <Paper
          elevation={0}
          sx={{
            p: 4,
            maxWidth: 500,
            textAlign: "center",
            borderRadius: "16px",
            border: `1px solid ${isDark ? "#1e293b" : "#e5e7eb"}`,
            backgroundColor: isDark ? "rgba(15,23,42,0.6)" : "#ffffff",
          }}
        >
          <Typography variant="h6" fontWeight={700} color="error" gutterBottom>
            Access Denied
          </Typography>
          <Typography variant="body2" color="text.secondary">
            You do not have permission to view requisitions.
          </Typography>
        </Paper>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        minHeight: "100vh",
        // background: isDark
        //   ? "linear-gradient(135deg, #0a0f1e 0%, #0f172a 50%, #1e1b2e 100%)"
        //   : "linear-gradient(135deg, #fffbeb 0%, #ffffff 50%, #fefce8 100%)",
        p: 3,
      }}
    >
      <Box sx={{ mb: 4 }}>
        <Stack
          direction={{ xs: "column", sm: "row" }}
          spacing={2}
          alignItems={{ sm: "center" }}
          justifyContent="space-between"
          sx={{ width: "100%" }}
        >
          <Stack
            direction="row"
            spacing={2}
            alignItems="center"
            sx={{ flex: 1 }}
          >
            <Box
              component="img"
              src={rolesSvg}
              alt="Requisitions"
              sx={{
                width: { xs: 64, lg: 80 },
                height: { xs: 64, lg: 80 },
                objectFit: "contain",
              }}
              onError={(event) => {
                event.currentTarget.style.display = "none";
              }}
            />
            <Box>
              <Typography
                variant="h4"
                component="h1"
                sx={{
                  fontWeight: "bold",
                  color: "text.primary",
                }}
              >
                Requisitions
              </Typography>
              <Typography
                variant="body2"
                sx={{
                  color: "text.secondary",
                  mt: 0.5,
                }}
              >
                Manage all requisitions
              </Typography>
            </Box>
          </Stack>

          <Stack direction="row" spacing={1.5} alignItems="center">
            {/* <Button
              variant="outlined"
              onClick={() => navigate("/company/my-requisitions")}
              sx={{
                height: 50,
                borderRadius: "10px",
                textTransform: "none",
                fontWeight: 700,
                borderColor: isDark ? "#334155" : "#cbd5e1",
                color: isDark ? "#f3f4f6" : "#334155",
                "&:hover": {
                  borderColor: "#d97706",
                  backgroundColor: "rgba(217, 119, 6, 0.08)",
                },
              }}
            >
              My Requisitions
            </Button> */}
            <Button
              variant="outlined"
              onClick={() => navigate("/company/requisition-workflows")}
              startIcon={<WorkflowIcon sx={{ fontSize: 20 }} />}
              sx={{
                height: 50,
                borderRadius: "10px",
                textTransform: "none",
                fontWeight: 700,
                borderColor: isDark ? "#334155" : "#cbd5e1",
                color: isDark ? "#f3f4f6" : "#334155",
                "&:hover": {
                  borderColor: "#4f46e5",
                  backgroundColor: "rgba(79, 70, 229, 0.08)",
                  color: "#4f46e5",
                },
              }}
            >
              Approval Workflow
            </Button>
            {/* {canCreate && (
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={() => navigate("/company/requisition-create")}
                sx={{
                  height: 50,
                  borderRadius: "10px",
                  textTransform: "none",
                  fontWeight: 700,
                  backgroundColor:
                    "linear-gradient(135deg, #d97706 0%, #b45309 100%)",
                  boxShadow: "0 10px 15px -3px rgba(245, 158, 11, 0.25)",
                  "&:hover": { backgroundColor: "#d97706" },
                }}
              >
                Create Requisition
              </Button>
            )} */}
          </Stack>
        </Stack>
      </Box>

      <Stack
        direction={{ xs: "column", sm: "row" }}
        spacing={2}
        alignItems={{ sm: "center" }}
        justifyContent="space-between"
        sx={{ mb: 3, flexWrap: "wrap" }}
      >
        <TextField
          placeholder="Search requisitions..."
          value={searchTerm}
          onChange={(event) => {
            setSearchTerm(event.target.value);
            setPage(0);
          }}
          variant="outlined"
          size="small"
          sx={{
            width: { xs: "100%", sm: 260 },
            "& .MuiOutlinedInput-root": {
              borderRadius: "12px",
              bgcolor: "background.paper",
              "& fieldset": {
                borderColor: isDark ? "#334155" : "#e5e7eb",
              },
              "&:hover fieldset": {
                borderColor: "#f59e0b",
              },
              "&.Mui-focused fieldset": {
                borderColor: "#f59e0b",
              },
            },
          }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon sx={{ color: "#9ca3af" }} />
              </InputAdornment>
            ),
          }}
        />

        <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
          {statusFilters.map((filter) => (
            <Button
              key={filter}
              variant={statusFilter === filter ? "contained" : "outlined"}
              onClick={() => {
                setStatusFilter(filter);
                setPage(0);
              }}
              size="small"
              sx={{
                textTransform: "capitalize",
                borderRadius: "8px",
                ...(statusFilter === filter && {
                  background:
                    "linear-gradient(135deg, #f59e0b 0%, #d97706 100%)",
                  color: "#ffffff",
                  boxShadow: "0 4px 6px -1px rgba(245, 158, 11, 0.25)",
                  "&:hover": {
                    background:
                      "linear-gradient(135deg, #d97706 0%, #b45309 100%)",
                  },
                }),
                ...(statusFilter !== filter && {
                  borderColor: isDark ? "#475569" : "#d1d5db",
                  color: isDark ? "#d1d5db" : "#6b7280",
                  "&:hover": {
                    borderColor: "#f59e0b",
                    backgroundColor: isDark
                      ? "rgba(245,158,11,0.1)"
                      : "#fffbeb",
                  },
                }),
              }}
            >
              {filter.charAt(0).toUpperCase() + filter.slice(1).toLowerCase()}
            </Button>
          ))}
        </Stack>
      </Stack>

      {error && (
        <Paper
          sx={{
            p: 2,
            mb: 3,
            backgroundColor: "rgba(239,68,68,0.1)",
            borderLeft: "4px solid #ef4444",
          }}
        >
          <Typography sx={{ color: "#ef4444", fontSize: "0.9rem" }}>
            {error}
          </Typography>
        </Paper>
      )}

      {loading ? (
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            height: 400,
          }}
        >
          <Stack alignItems="center" spacing={2}>
            <CircularProgress
              size={48}
              sx={{
                color: "primary.main",
                "& .MuiCircularProgress-circle": {
                  strokeLinecap: "round",
                },
              }}
            />
            <Typography sx={{ color: "text.secondary" }}>
              Loading requisitions...
            </Typography>
          </Stack>
        </Box>
      ) : (
        <Paper
          elevation={0}
          sx={{
            borderRadius: "16px",
            overflow: "hidden",
            boxShadow: isDark
              ? "0 10px 25px -5px rgba(0,0,0,0.3)"
              : "0 10px 25px -5px rgba(0,0,0,0.05), 0 8px 10px -6px rgba(0,0,0,0.02)",
            border: `1px solid ${isDark ? "#1e293b" : "#f3f4f6"}`,
          }}
        >
          <TableContainer sx={{ overflowX: "auto" }}>
            <Table sx={{ minWidth: 900 }}>
              <TableHead
                sx={{ backgroundColor: isDark ? "#1e293b" : "#f9fafb" }}
              >
                <TableRow>
                  <TableCell sx={{ width: 48 }} />
                  <TableCell sx={{ fontWeight: 600, fontSize: "0.85rem" }}>
                    Requisition
                  </TableCell>
                  <TableCell sx={{ fontWeight: 600, fontSize: "0.85rem" }}>
                    Type
                  </TableCell>
                  <TableCell sx={{ fontWeight: 600, fontSize: "0.85rem" }}>
                    Department / Project
                  </TableCell>
                  <TableCell sx={{ fontWeight: 600, fontSize: "0.85rem" }}>
                    Total Amount
                  </TableCell>
                  <TableCell sx={{ fontWeight: 600, fontSize: "0.85rem" }}>
                    Requested By
                  </TableCell>
                  <TableCell sx={{ fontWeight: 600, fontSize: "0.85rem" }}>
                    Requested At
                  </TableCell>
                  <TableCell sx={{ fontWeight: 600, fontSize: "0.85rem" }}>
                    Status
                  </TableCell>
                  {canAction && (
                    <TableCell sx={{ fontWeight: 600, fontSize: "0.85rem" }}>
                      Actions & Remarks
                    </TableCell>
                  )}
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredRequisitions.length === 0 ? (
                  <TableRow>
                    <TableCell
                      colSpan={canAction ? 9 : 8}
                      align="center"
                      sx={{ py: 6 }}
                    >
                      <DescriptionIcon
                        sx={{ fontSize: 48, color: "#d1d5db", mb: 1 }}
                      />
                      <Typography
                        sx={{
                          color: "text.secondary",
                          align: "center",
                          fontSize: "0.875rem",
                        }}
                      >
                        No requisitions found
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  filteredRequisitions
                    .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                    .map((req, index) => {
                      const requisitionId = req.id;
                      const isExpanded = Boolean(expandedRows[requisitionId]);
                      const detail = requisitionDetails[requisitionId];
                      const detailLoading = detailsLoading[requisitionId];
                      const detailError = detailsError[requisitionId];
                      const statusColor = getStatusColor(
                        req.status,
                        theme.palette.mode,
                      );

                      return (
                        <React.Fragment
                          key={requisitionId || req.requisition_no}
                        >
                          <TableRow
                            component={motion.tr}
                            initial={{ opacity: 0, x: -20 }}
                            animate={{ opacity: 1, x: 0 }}
                            transition={{ delay: index * 0.05 }}
                            sx={{
                              "&:hover": {
                                backgroundColor: isDark
                                  ? "rgba(30,41,59,0.5)"
                                  : "#f9fafb",
                              },
                            }}
                          >
                            <TableCell>
                              <IconButton
                                aria-label={
                                  isExpanded
                                    ? "Hide requisition details"
                                    : "Show requisition details"
                                }
                                size="small"
                                onClick={() => handleToggleDetails(req)}
                                disabled={!requisitionId}
                              >
                                {isExpanded ? (
                                  <KeyboardArrowDownIcon />
                                ) : (
                                  <KeyboardArrowRightIcon />
                                )}
                              </IconButton>
                            </TableCell>
                            <TableCell>
                              <Stack spacing={0.5}>
                                <Typography
                                  sx={{
                                    fontFamily: "monospace",
                                    fontWeight: 700,
                                    fontSize: "0.875rem",
                                    color: "primary.main",
                                  }}
                                >
                                  {getDisplayValue(req.requisition_no, req.id)}
                                </Typography>
                                <Typography
                                  sx={{
                                    color: "text.secondary",
                                    fontSize: "0.75rem",
                                  }}
                                >
                                  ID: {getDisplayValue(req.id)}
                                </Typography>
                              </Stack>
                            </TableCell>
                            <TableCell>
                              <Typography
                                sx={{ fontSize: "0.875rem", fontWeight: 600 }}
                              >
                                {getDisplayValue(req.requisition_type)}
                              </Typography>
                            </TableCell>
                            <TableCell>
                              <Stack spacing={0.5}>
                                <Typography
                                  sx={{
                                    fontWeight: 500,
                                    color: "text.primary",
                                  }}
                                >
                                  {getDisplayValue(req.department_name)}
                                </Typography>
                                <Typography
                                  sx={{
                                    color: "text.secondary",
                                    fontSize: "0.75rem",
                                  }}
                                >
                                  Project: {getDisplayValue(req.project_name)}
                                </Typography>
                              </Stack>
                            </TableCell>
                            <TableCell>
                              <Typography
                                sx={{ fontWeight: 600, color: "#10b981" }}
                              >
                                {formatCurrency(
                                  getDisplayValue(req.total_amount, req.amount),
                                )}
                              </Typography>
                            </TableCell>
                            <TableCell>
                              <Typography sx={{ fontSize: "0.875rem" }}>
                                {getDisplayValue(
                                  req.requested_by_name,
                                  "Unknown",
                                )}
                              </Typography>
                            </TableCell>
                            <TableCell>
                              <Typography sx={{ fontSize: "0.875rem" }}>
                                {formatDate(req.requested_at)}
                              </Typography>
                            </TableCell>
                            <TableCell>
                              <Chip
                                label={getDisplayValue(req.status)}
                                size="small"
                                sx={{
                                  backgroundColor: statusColor.bg,
                                  color: statusColor.text,
                                  fontWeight: 600,
                                  fontSize: "0.75rem",
                                }}
                              />
                            </TableCell>
                            {canAction && (
                              <TableCell align="right">
                                {["SUBMITTED", "PENDING"].includes(
                                  String(req.status).toUpperCase(),
                                ) ? (
                                  <Stack
                                    direction="row"
                                    spacing={1}
                                    justifyContent="flex-end"
                                  >
                                    {/* Approve button — only shown when backend says this user can approve this specific requisition */}
                                    {req.canApprove && (
                                      <Button
                                        size="small"
                                        variant="contained"
                                        startIcon={<CheckCircleIcon />}
                                        disabled={Boolean(
                                          actionLoading[requisitionId],
                                        )}
                                        onClick={() =>
                                          openActionDialog(req, "APPROVE")
                                        }
                                        sx={{
                                          borderRadius: "8px",
                                          textTransform: "none",
                                          fontWeight: 700,
                                          backgroundColor: "#16a34a",
                                          "&:hover": {
                                            backgroundColor: "#15803d",
                                          },
                                        }}
                                      >
                                        Accept
                                      </Button>
                                    )}
                                    {/* Reject button — same per-row flag */}
                                    {req.canApprove && (
                                      <Button
                                        size="small"
                                        variant="outlined"
                                        startIcon={<CancelIcon />}
                                        disabled={Boolean(
                                          actionLoading[requisitionId],
                                        )}
                                        onClick={() =>
                                          openActionDialog(req, "REJECT")
                                        }
                                        sx={{
                                          borderRadius: "8px",
                                          textTransform: "none",
                                          fontWeight: 700,
                                          borderColor: "#ef4444",
                                          color: "#ef4444",
                                          "&:hover": {
                                            borderColor: "#dc2626",
                                            backgroundColor:
                                              "rgba(239,68,68,0.08)",
                                          },
                                        }}
                                      >
                                        Reject
                                      </Button>
                                    )}
                                    {/* {canEdit && (
                                      <Button
                                        size="small"
                                        variant="outlined"
                                        startIcon={<EditOutlinedIcon />}
                                        disabled={Boolean(
                                          actionLoading[requisitionId],
                                        )}
                                        onClick={() =>
                                          setEditModal({
                                            open: true,
                                            requisitionId: req.id,
                                          })
                                        }
                                        sx={{
                                          borderRadius: "8px",
                                          textTransform: "none",
                                          fontWeight: 700,
                                          borderColor: "#d97706",
                                          color: "#d97706",
                                          "&:hover": {
                                            borderColor: "#b45309",
                                            backgroundColor:
                                              "rgba(217,119,6,0.08)",
                                          },
                                        }}
                                      >
                                        Edit
                                      </Button>
                                    )} */}
                                  </Stack>
                                ) : (
                                  <Typography
                                    sx={{
                                      color: "text.secondary",
                                      fontSize: "0.8rem",
                                      fontStyle: "italic",
                                      textAlign: "center",
                                    }}
                                  >
                                    {req.remarks || ""} ({req.status})
                                  </Typography>
                                )}
                              </TableCell>
                            )}
                          </TableRow>
                          <TableRow>
                            <TableCell
                              colSpan={canAction ? 9 : 8}
                              sx={{
                                p: 0,
                                borderBottom: isExpanded ? undefined : 0,
                                backgroundColor: isDark
                                  ? "rgba(15,23,42,0.75)"
                                  : "#fffbeb",
                              }}
                            >
                              <Collapse
                                in={isExpanded}
                                timeout="auto"
                                unmountOnExit
                              >
                                <Box sx={{ p: 3 }}>
                                  {detailLoading ? (
                                    <Stack
                                      direction="row"
                                      spacing={1.5}
                                      alignItems="center"
                                    >
                                      <CircularProgress
                                        size={20}
                                        sx={{ color: "primary.main" }}
                                      />
                                      <Typography sx={{ fontSize: "0.875rem" }}>
                                        Loading requisition details...
                                      </Typography>
                                    </Stack>
                                  ) : detailError ? (
                                    <Typography
                                      sx={{
                                        color: "#ef4444",
                                        fontSize: "0.875rem",
                                      }}
                                    >
                                      {detailError}
                                    </Typography>
                                  ) : detail ? (
                                    <Stack spacing={2.5}>
                                      <Stack
                                        direction={{ xs: "column", md: "row" }}
                                        spacing={2}
                                        divider={
                                          <Divider
                                            orientation="vertical"
                                            flexItem
                                          />
                                        }
                                      >
                                        <Box sx={{ flex: 1 }}>
                                          <Typography
                                            sx={{
                                              fontSize: "0.75rem",
                                              color: "#6b7280",
                                              mb: 0.5,
                                            }}
                                          >
                                            Description
                                          </Typography>
                                          <Typography
                                            sx={{
                                              fontSize: "0.9rem",
                                              fontWeight: 500,
                                            }}
                                          >
                                            {getDisplayValue(
                                              detail.description,
                                            )}
                                          </Typography>
                                        </Box>
                                        <Box sx={{ flex: 1 }}>
                                          <Typography
                                            sx={{
                                              fontSize: "0.75rem",
                                              color: "#6b7280",
                                              mb: 0.5,
                                            }}
                                          >
                                            Requested By
                                          </Typography>
                                          <Typography
                                            sx={{
                                              fontSize: "0.9rem",
                                              fontWeight: 500,
                                            }}
                                          >
                                            {getDisplayValue(
                                              detail.requested_by_name,
                                            )}
                                          </Typography>
                                        </Box>
                                        <Box sx={{ flex: 1 }}>
                                          <Typography
                                            sx={{
                                              fontSize: "0.75rem",
                                              color: "#6b7280",
                                              mb: 0.5,
                                            }}
                                          >
                                            Created / Updated
                                          </Typography>
                                          <Typography
                                            sx={{
                                              fontSize: "0.9rem",
                                              fontWeight: 500,
                                            }}
                                          >
                                            {formatDate(detail.created_at)} /{" "}
                                            {formatDate(detail.updated_at)}
                                          </Typography>
                                        </Box>
                                      </Stack>

                                      <Box>
                                        <Typography
                                          sx={{
                                            fontSize: "0.95rem",
                                            fontWeight: 700,
                                            mb: 1,
                                          }}
                                        >
                                          Items
                                        </Typography>
                                        <TableContainer
                                          component={Paper}
                                          elevation={0}
                                          sx={{
                                            border: `1px solid ${
                                              isDark ? "#334155" : "#fde68a"
                                            }`,
                                            borderRadius: "8px",
                                          }}
                                        >
                                          <Table size="small">
                                            <TableHead>
                                              <TableRow>
                                                <TableCell>Item</TableCell>
                                                <TableCell>
                                                  Description
                                                </TableCell>
                                                <TableCell align="right">
                                                  Quantity
                                                </TableCell>
                                                <TableCell align="right">
                                                  Unit Rate
                                                </TableCell>
                                                <TableCell align="right">
                                                  Amount
                                                </TableCell>
                                                <TableCell>
                                                  Required By
                                                </TableCell>
                                              </TableRow>
                                            </TableHead>
                                            <TableBody>
                                              {(detail.items || []).length ===
                                              0 ? (
                                                <TableRow>
                                                  <TableCell
                                                    colSpan={6}
                                                    align="center"
                                                  >
                                                    No items found
                                                  </TableCell>
                                                </TableRow>
                                              ) : (
                                                detail.items.map((item) => (
                                                  <TableRow
                                                    key={
                                                      item.id || item.item_name
                                                    }
                                                  >
                                                    <TableCell>
                                                      {getDisplayValue(
                                                        item.item_name,
                                                      )}
                                                    </TableCell>
                                                    <TableCell>
                                                      {getDisplayValue(
                                                        item.item_description,
                                                      )}
                                                    </TableCell>
                                                    <TableCell align="right">
                                                      {getDisplayValue(
                                                        item.quantity,
                                                      )}
                                                    </TableCell>
                                                    <TableCell align="right">
                                                      {formatCurrency(
                                                        item.unit_rate,
                                                      )}
                                                    </TableCell>
                                                    <TableCell align="right">
                                                      {formatCurrency(
                                                        item.amount,
                                                      )}
                                                    </TableCell>
                                                    <TableCell>
                                                      {formatDate(
                                                        item.required_by,
                                                      )}
                                                    </TableCell>
                                                  </TableRow>
                                                ))
                                              )}
                                            </TableBody>
                                          </Table>
                                        </TableContainer>
                                      </Box>
                                      {/* Workflow Stepper */}
                                      {detail.workflow_steps &&
                                        detail.workflow_steps.length > 0 && (
                                          <Box
                                            sx={{
                                              borderRadius: "12px",
                                              border: `1px solid ${
                                                isDark ? "#1e293b" : "#e5e7eb"
                                              }`,
                                              p: 2.5,
                                              backgroundColor: isDark
                                                ? "rgba(15,23,42,0.4)"
                                                : "#fafafa",
                                            }}
                                          >
                                            <WorkflowStepper
                                              steps={detail.workflow_steps}
                                              isDark={isDark}
                                            />
                                          </Box>
                                        )}

                                      {/* Confidential Step Form Submissions (Exclusive to Next Approver / Admin) */}
                                      {detail.confidential_form_submissions &&
                                        detail.confidential_form_submissions
                                          .length > 0 && (
                                          <Stack spacing={2} sx={{ mt: 1 }}>
                                            {detail.confidential_form_submissions.map(
                                              (sub, sIdx) => (
                                                <Paper
                                                  key={sIdx}
                                                  elevation={0}
                                                  sx={{
                                                    p: 2.5,
                                                    borderRadius: "12px",
                                                    border: `1.5px solid ${
                                                      isDark
                                                        ? "rgba(99,102,241,0.35)"
                                                        : "#c7d2fe"
                                                    }`,
                                                    backgroundColor: isDark
                                                      ? "rgba(30,27,75,0.25)"
                                                      : "#f5f3ff",
                                                  }}
                                                >
                                                  <Stack
                                                    direction={{
                                                      xs: "column",
                                                      sm: "row",
                                                    }}
                                                    spacing={1}
                                                    justifyContent="space-between"
                                                    alignItems={{
                                                      sm: "center",
                                                    }}
                                                    sx={{ mb: 1.5 }}
                                                  >
                                                    <Stack
                                                      direction="row"
                                                      spacing={1}
                                                      alignItems="center"
                                                    >
                                                      <Box
                                                        sx={{
                                                          p: 0.8,
                                                          borderRadius: "8px",
                                                          backgroundColor:
                                                            isDark
                                                              ? "rgba(99,102,241,0.2)"
                                                              : "#ede9fe",
                                                          color: "#6366f1",
                                                          display: "flex",
                                                        }}
                                                      >
                                                        <DynamicFormIcon fontSize="small" />
                                                      </Box>
                                                      <Box>
                                                        <Typography
                                                          variant="subtitle2"
                                                          fontWeight={800}
                                                          color="#4338ca"
                                                        >
                                                          {sub.form_name}
                                                        </Typography>
                                                        <Typography
                                                          variant="caption"
                                                          sx={{
                                                            color: isDark
                                                              ? "#a5b4fc"
                                                              : "#6366f1",
                                                            fontWeight: 600,
                                                          }}
                                                        >
                                                          🔒 Confidential Step
                                                          Form (Submitted by{" "}
                                                          {sub.submitted_by_name ||
                                                            `Employee #${sub.submitted_by}`}
                                                          )
                                                        </Typography>
                                                      </Box>
                                                    </Stack>
                                                    <Chip
                                                      size="small"
                                                      label={
                                                        sub.submitted_at
                                                          ? new Date(
                                                              sub.submitted_at,
                                                            ).toLocaleString()
                                                          : "Submitted"
                                                      }
                                                      sx={{
                                                        fontSize: "0.7rem",
                                                        fontWeight: 600,
                                                      }}
                                                    />
                                                  </Stack>

                                                  <Divider
                                                    sx={{
                                                      mb: 2,
                                                      borderColor: isDark
                                                        ? "rgba(99,102,241,0.2)"
                                                        : "#ddd6fe",
                                                    }}
                                                  />

                                                  {/* Dynamic Questions & Answers Grid */}
                                                  <Box
                                                    sx={{
                                                      display: "grid",
                                                      gridTemplateColumns: {
                                                        xs: "1fr",
                                                        sm: "1fr 1fr",
                                                        md: "1fr 1fr 1fr",
                                                      },
                                                      gap: 1.5,
                                                    }}
                                                  >
                                                    {sub.values &&
                                                      sub.values.map(
                                                        (valItem, vIdx) => {
                                                          if (
                                                            valItem.field_type ===
                                                            "table"
                                                          ) {
                                                            let rows = [];
                                                            try {
                                                              rows =
                                                                typeof valItem.value ===
                                                                "string"
                                                                  ? JSON.parse(
                                                                      valItem.value,
                                                                    )
                                                                  : valItem.value ||
                                                                    [];
                                                            } catch (e) {
                                                              rows = [];
                                                            }
                                                            return (
                                                              <Box
                                                                key={vIdx}
                                                                sx={{
                                                                  gridColumn:
                                                                    "1 / -1",
                                                                  width: "100%",
                                                                  mt: 1,
                                                                }}
                                                              >
                                                                <DynamicTableField
                                                                  label={
                                                                    valItem.field_label ||
                                                                    valItem.field_name
                                                                  }
                                                                  columns={
                                                                    valItem.options
                                                                  }
                                                                  value={rows}
                                                                  readOnly={
                                                                    true
                                                                  }
                                                                  isDark={
                                                                    isDark
                                                                  }
                                                                />
                                                              </Box>
                                                            );
                                                          }

                                                          return (
                                                            <Box
                                                              key={vIdx}
                                                              sx={{
                                                                p: 1.5,
                                                                borderRadius:
                                                                  "8px",
                                                                backgroundColor:
                                                                  isDark
                                                                    ? "rgba(15,23,42,0.6)"
                                                                    : "#ffffff",
                                                                border: `1px solid ${
                                                                  isDark
                                                                    ? "#334155"
                                                                    : "#e2e8f0"
                                                                }`,
                                                              }}
                                                            >
                                                              <Typography
                                                                variant="caption"
                                                                sx={{
                                                                  color: isDark
                                                                    ? "#94a3b8"
                                                                    : "#64748b",
                                                                  fontWeight: 700,
                                                                  textTransform:
                                                                    "uppercase",
                                                                  letterSpacing:
                                                                    "0.04em",
                                                                  display:
                                                                    "block",
                                                                  mb: 0.5,
                                                                }}
                                                              >
                                                                {valItem.field_label ||
                                                                  valItem.field_name}
                                                              </Typography>
                                                              <Typography
                                                                variant="body2"
                                                                fontWeight={700}
                                                                sx={{
                                                                  color: isDark
                                                                    ? "#f1f5f9"
                                                                    : "#1e293b",
                                                                }}
                                                              >
                                                                {valItem.field_type ===
                                                                "boolean"
                                                                  ? valItem.value ===
                                                                      "TRUE" ||
                                                                    valItem.value ===
                                                                      "true" ||
                                                                    valItem.value ===
                                                                      "1"
                                                                    ? "✅ Yes"
                                                                    : "❌ No"
                                                                  : valItem.value ||
                                                                    "—"}
                                                              </Typography>
                                                            </Box>
                                                          );
                                                        },
                                                      )}
                                                  </Box>
                                                </Paper>
                                              ),
                                            )}
                                          </Stack>
                                        )}
                                    </Stack>
                                  ) : null}
                                </Box>
                              </Collapse>
                            </TableCell>
                          </TableRow>
                        </React.Fragment>
                      );
                    })
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      )}

      {/* Pagination */}
      {!loading && safeRequisitions.length > 0 && (
        <TablePagination
          rowsPerPageOptions={[5, 10, 25, 50]}
          component="div"
          count={filteredRequisitions.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          sx={{
            mt: 2,
            color: "text.secondary",
            ".MuiTablePagination-selectIcon, .MuiTablePagination-actions button":
              {
                color: "text.secondary",
              },
          }}
        />
      )}

      <Dialog
        open={actionDialog.open}
        onClose={closeActionDialog}
        fullWidth
        maxWidth="sm"
        PaperProps={{
          sx: {
            borderRadius: "14px",
            backgroundColor: isDark ? "#0f172a" : "#ffffff",
          },
        }}
      >
        <DialogTitle
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            color: "text.primary",
            fontWeight: 800,
          }}
        >
          {actionDialog.action === "APPROVE"
            ? "Accept Requisition"
            : "Reject Requisition"}
          <IconButton size="small" onClick={closeActionDialog}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <Typography sx={{ color: isDark ? "#cbd5e1" : "#4b5563", mb: 2 }}>
            {actionDialog.requisition?.requisition_no || "This requisition"}{" "}
            will be marked as{" "}
            {actionDialog.action === "APPROVE" ? "approved" : "rejected"}.
          </Typography>
          <TextField
            fullWidth
            multiline
            minRows={3}
            label={
              actionDialog.action === "APPROVE"
                ? "Remarks"
                : "Rejection remarks"
            }
            value={actionDialog.remarks}
            onChange={(event) =>
              setActionDialog((prev) => ({
                ...prev,
                remarks: event.target.value,
              }))
            }
            error={
              actionDialog.action === "REJECT" && !actionDialog.remarks.trim()
            }
            helperText={
              actionDialog.action === "REJECT" && !actionDialog.remarks.trim()
                ? "Remarks are required to reject"
                : ""
            }
            sx={{
              "& .MuiOutlinedInput-root": {
                borderRadius: "10px",
                backgroundColor: isDark ? "#1e293b" : "#f9fafb",
              },
            }}
          />

          {/* Dynamic Form for Approver (Step N Form) */}
          {actionDialog.action === "APPROVE" && actionDialog.formLoading && (
            <Box
              sx={{ display: "flex", alignItems: "center", gap: 1.5, py: 2 }}
            >
              <CircularProgress size={20} />
              <Typography variant="body2" color="text.secondary">
                Loading required approval form...
              </Typography>
            </Box>
          )}

          {actionDialog.action === "APPROVE" && actionDialog.dynamicForm && (
            <Box
              sx={{
                mt: 2.5,
                p: 2,
                borderRadius: "12px",
                border: `1.5px solid ${isDark ? "#334155" : "#e0e7ff"}`,
                backgroundColor: isDark ? "rgba(30,41,59,0.3)" : "#f5f7ff",
              }}
            >
              <Stack
                direction="row"
                spacing={1}
                alignItems="center"
                sx={{ mb: 1.5 }}
              >
                <DynamicFormIcon fontSize="small" sx={{ color: "#4f46e5" }} />
                <Typography
                  variant="subtitle2"
                  fontWeight={800}
                  color="#4338ca"
                >
                  {actionDialog.dynamicForm.form_name}
                </Typography>
                <Chip
                  size="small"
                  label="Required Form"
                  color="primary"
                  sx={{ height: 20, fontSize: "0.65rem", fontWeight: 700 }}
                />
              </Stack>

              {actionDialog.dynamicForm.description && (
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ display: "block", mb: 2 }}
                >
                  {actionDialog.dynamicForm.description}
                </Typography>
              )}

              <Stack spacing={2}>
                {actionDialog.dynamicForm.fields &&
                  actionDialog.dynamicForm.fields.map((field) => {
                    const val = actionDialog.formData[field.id];
                    const isRequired = field.required;

                    if (field.field_type === "table") {
                      return (
                        <DynamicTableField
                          key={field.id}
                          label={field.field_label || field.field_name}
                          columns={field.options}
                          value={Array.isArray(val) ? val : []}
                          onChange={(newRows) =>
                            setActionDialog((prev) => ({
                              ...prev,
                              formData: {
                                ...prev.formData,
                                [field.id]: newRows,
                              },
                            }))
                          }
                          required={isRequired}
                          isDark={isDark}
                        />
                      );
                    }

                    if (field.field_type === "boolean") {
                      return (
                        <FormControlLabel
                          key={field.id}
                          control={
                            <Switch
                              checked={Boolean(val)}
                              onChange={(e) =>
                                setActionDialog((prev) => ({
                                  ...prev,
                                  formData: {
                                    ...prev.formData,
                                    [field.id]: e.target.checked,
                                  },
                                }))
                              }
                            />
                          }
                          label={
                            <Typography variant="body2" fontWeight={600}>
                              {field.field_label} {isRequired && "*"}
                            </Typography>
                          }
                        />
                      );
                    }

                    if (field.field_type === "select") {
                      const opts = Array.isArray(field.options)
                        ? field.options
                        : [];
                      return (
                        <FormControl
                          key={field.id}
                          size="small"
                          fullWidth
                          required={isRequired}
                        >
                          <InputLabel>{field.field_label}</InputLabel>
                          <Select
                            value={val || ""}
                            label={field.field_label}
                            onChange={(e) =>
                              setActionDialog((prev) => ({
                                ...prev,
                                formData: {
                                  ...prev.formData,
                                  [field.id]: e.target.value,
                                },
                              }))
                            }
                          >
                            {opts.map((opt, oIdx) => (
                              <MenuItem key={oIdx} value={opt}>
                                {opt}
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                      );
                    }

                    return (
                      <TextField
                        key={field.id}
                        size="small"
                        fullWidth
                        type={field.field_type === "number" ? "number" : "text"}
                        multiline={field.field_type === "textarea"}
                        rows={field.field_type === "textarea" ? 3 : 1}
                        label={`${field.field_label}${isRequired ? " *" : ""}`}
                        value={val || ""}
                        onChange={(e) =>
                          setActionDialog((prev) => ({
                            ...prev,
                            formData: {
                              ...prev.formData,
                              [field.id]: e.target.value,
                            },
                          }))
                        }
                        required={isRequired}
                      />
                    );
                  })}
              </Stack>
            </Box>
          )}

          {/* File Upload for Approval */}
          {actionDialog.action === "APPROVE" && (
            <Box sx={{ mt: 2.5 }}>
              <Typography
                variant="body2"
                sx={{
                  fontWeight: 600,
                  fontSize: "0.82rem",
                  color: isDark ? "#e2e8f0" : "#334155",
                  mb: 0.75,
                }}
              >
                {Number(
                  actionDialog.requisition?.pending_step_is_file_required,
                ) === 1
                  ? "Attachment (Mandatory) *"
                  : "Attachment (Optional)"}
              </Typography>
              <input
                type="file"
                id="approval-file-upload-input"
                style={{ display: "none" }}
                onChange={(e) => {
                  const f = e.target.files?.[0] || null;
                  setActionDialog((prev) => ({ ...prev, file: f }));
                }}
              />
              <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                <label htmlFor="approval-file-upload-input">
                  <Button
                    variant="outlined"
                    component="span"
                    size="small"
                    startIcon={<DescriptionIcon sx={{ fontSize: 16 }} />}
                    sx={{
                      borderRadius: "8px",
                      textTransform: "none",
                      fontSize: "0.8rem",
                      fontWeight: 600,
                    }}
                  >
                    {actionDialog.file ? "Change File" : "Choose File"}
                  </Button>
                </label>
                {actionDialog.file ? (
                  <Stack
                    direction="row"
                    alignItems="center"
                    spacing={1}
                    sx={{ maxWidth: "60%" }}
                  >
                    <Typography
                      variant="caption"
                      sx={{
                        color: isDark ? "#94a3b8" : "#475569",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                        fontWeight: 500,
                      }}
                    >
                      {actionDialog.file.name}
                    </Typography>
                    <IconButton
                      size="small"
                      onClick={() =>
                        setActionDialog((prev) => ({ ...prev, file: null }))
                      }
                    >
                      <CloseIcon sx={{ fontSize: 14 }} />
                    </IconButton>
                  </Stack>
                ) : (
                  <Typography
                    variant="caption"
                    sx={{
                      color:
                        Number(
                          actionDialog.requisition
                            ?.pending_step_is_file_required,
                        ) === 1
                          ? "#ef4444"
                          : "#94a3b8",
                      fontSize: "0.75rem",
                    }}
                  >
                    {Number(
                      actionDialog.requisition?.pending_step_is_file_required,
                    ) === 1
                      ? "A file must be uploaded before accepting."
                      : "No file chosen"}
                  </Typography>
                )}
              </Box>
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 3 }}>
          <Button
            onClick={closeActionDialog}
            variant="outlined"
            sx={{ borderRadius: "9px", textTransform: "none", fontWeight: 700 }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleConfirmAction}
            disabled={
              Boolean(actionLoading[actionDialog.requisition?.id]) ||
              (actionDialog.action === "REJECT" &&
                !actionDialog.remarks.trim()) ||
              (actionDialog.action === "APPROVE" &&
                Number(
                  actionDialog.requisition?.pending_step_is_file_required,
                ) === 1 &&
                !actionDialog.file) ||
              Boolean(actionDialog.formLoading)
            }
            variant="contained"
            sx={{
              borderRadius: "9px",
              textTransform: "none",
              fontWeight: 800,
              backgroundColor:
                actionDialog.action === "APPROVE" ? "#16a34a" : "#ef4444",
              "&:hover": {
                backgroundColor:
                  actionDialog.action === "APPROVE" ? "#15803d" : "#dc2626",
              },
            }}
          >
            {actionLoading[actionDialog.requisition?.id] ? (
              <CircularProgress size={20} sx={{ color: "#ffffff" }} />
            ) : actionDialog.action === "APPROVE" ? (
              "Accept"
            ) : (
              "Reject"
            )}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit Requisition Modal */}
      {editModal.open && (
        <EditRequisitionModal
          open={editModal.open}
          requisitionId={editModal.requisitionId}
          onClose={() => setEditModal({ open: false, requisitionId: null })}
          onSuccess={() => dispatch(fetchRequisitions())}
        />
      )}
    </Box>
  );
};

export default Requisitions;
